import { ref, computed } from "vue";
import { useStore } from "vuex";
import { options, columns } from "@/helpers/historicalLogsHelper";

export default function useHistoricalLogsTable() {
  const store = useStore();

  const loading = computed(() => store.getters["historicalLogs/isLoading"]);
  const historicalLogs = computed(
    () => store.getters["historicalLogs/getHistoricalLogs"]
  );
  const pagination = computed(
    () => store.getters["historicalLogs/getPagination"]
  );
  const filters = computed(() => store.getters["historicalLogs/getFilters"]);

  const selectedLogs = ref([]);

  const tableOptions = {
    ...options,
    pageLength: pagination.value.per_page,
    ajax: (data, callback) => {
      if (!loading.value) {
        callback({
          draw: data.draw,
          recordsTotal: pagination.value.total,
          recordsFiltered: pagination.value.total,
          data: historicalLogs.value,
        });
      }
    },
    buttons: [
      {
        text: "Select All",
        action: function (_, dt) {
          dt.rows().select();
        },
      },
      {
        text: "Deselect All",
        action: function (_, dt) {
          dt.rows().deselect();
        },
      },
      {
        extend: "csvHtml5",
        text: "Export CSV",
        title: "Historical Logs",
        exportOptions: {
          columns: ":visible",
        },
        customizeData: (data) => {
          const dt = this;
          const selectedRows = dt.rows({ selected: true }).data().toArray();
          if (selectedRows.length > 0) {
            data.body = selectedRows.map((row) => Object.values(row));
          } else {
            const allRows = dt.rows().data().toArray();
            data.body = allRows.map((row) => Object.values(row));
          }
        },
      },
    ],
  };

  const fetchHistoricalLogs = (additionalParams = {}) => {
    store.dispatch("historicalLogs/fetchHistoricalLogs", additionalParams);
  };

  const onPageChange = ({ event }) => {
    const info = event.dt.page.info();
    const newPage = info.page + 1;
    store.dispatch("historicalLogs/setPagination", { current_page: newPage });
    fetchHistoricalLogs();
  };

  const handleSearch = ({ dt }) => {
    const dataTable = dt.api ? dt.api : dt;
    if (dataTable && typeof dataTable.search === "function") {
      const searchValue = dataTable.search();
      if (filters.value.search !== searchValue) {
        store.dispatch("historicalLogs/setFilters", {
          search: searchValue.trim() || "",
        });
        store.dispatch("historicalLogs/setPagination", {
          current_page: 1,
        });
        fetchHistoricalLogs();
      }
    }
  };

  const handleSortChange = ({ dt }) => {
    const dataTable = dt.api;
    if (dataTable && typeof dataTable.order === "function") {
      const order = dataTable.order();

      if (!order || order.length === 0) {
        if (
          filters.value.sort_by !== "time" ||
          filters.value.sort_order !== "desc"
        ) {
          store.dispatch("historicalLogs/setFilters", {
            sort_by: "time",
            sort_order: "desc",
          });

          fetchHistoricalLogs();
        }
        return;
      }

      const direction = order[0][1];
      const columnIndex = order[0][0];

      const sortField = direction === "" ? "time" : columns[columnIndex].data;
      const normalizedDirection = direction === "" ? "desc" : direction;

      if (
        filters.value.sort_by !== sortField ||
        filters.value.sort_order !== normalizedDirection
      ) {
        store.dispatch("historicalLogs/setFilters", {
          sort_by: sortField,
          sort_order: normalizedDirection,
        });
        fetchHistoricalLogs();
      }
    }
  };

  const handleLengthChange = ({ dt }) => {
    const newLength = dt._iDisplayLength;

    store.dispatch("historicalLogs/setPagination", {
      per_page: newLength,
      current_page: 1,
    });

    fetchHistoricalLogs();
    dt.api.page(pagination.value.current_page - 1).draw(false);
  };

  const onSelect = ({ dt }) => {
    const selectedRows = dt.rows({ selected: true }).data();
    selectedLogs.value = selectedRows.toArray();
  };

  const onDeselect = ({ dt }) => {
    const selectedRows = dt.rows({ selected: true }).data();
    selectedLogs.value = selectedRows.toArray();
  };

  const resetTable = () => {
    store.dispatch("historicalLogs/resetHistoricalLogs");
    selectedLogs.value = [];
  };

  return {
    historicalLogs,
    tableOptions,
    columns,
    fetchHistoricalLogs,
    resetTable,
    onPageChange,
    handleSearch,
    handleSortChange,
    handleLengthChange,
    onSelect,
    onDeselect,
  };
}
